import { LoaderFunction, Params, redirect } from 'react-router-dom';
import { AuthContextType } from '../../../../../hooks/useAuth';

// Redux
import { store } from '../../../../../state/configureStore';
import { getCursor } from '../../../../../state/selectors/ui/tables';

// Types
import {
  tableIds,
  takeBase,
} from '../../../../../components/display/tables/types';
import { firstPageTake } from '../../../../../utils/cursor-take.helper';

// GQL
import { client } from '../../../../../gql/client';
import { selectWorkOrdersWithMultimedia } from '../../../../../gql/selectors/multimedia';
import {
  FETCH_ORDERS,
  GET_SELECTORS_DATA,
  GET_CONCESSIONS,
} from '../../../../../gql/queries/orders/orders';
import {
  FETCH_CAPITOLS,
  FETCH_SUB_CAPITOLS,
  FETCH_CLASSIFICATIONS,
  FETCH_PREDEFINED_ORDERS,
} from '../../../../../gql/queries/orders/predefinedData';
import { FETCH_USERS } from '../../../../../gql/queries/users';
import { loadingData } from '../../../../../state/actions/ui/loadingData';
import { setCursor } from '../../../../../state/actions/ui/cursorTables';
import { refreshOrderFlags } from '../../../../../gql/services/orderFlags';

// Factoría: el loader global es `makeLoader()` sin args (byte-idéntico al de
// siempre); las pestañas de un LISTADO de módulo pasan `containerTypes` +
// el `tableId` de su cursor dedicado.
//
// `getContainerId` (resolver el container desde `params`) se quedó SIN
// llamadores al retirarse las pestañas del detalle de campaña. Se conserva
// la opción, que es lo que hace reutilizable esta factoría.
//
// Fase 3 nav Trabajo/Revisión (rework, 2026-07-12) — `containerTypes` es
// ESTÁTICO (no depende de `params`): la pestaña "Multimedia" del listado del
// módulo RV filtra por TIPO de origen (`['surveillanceRoute']`), no por un
// `:routeId` de la URL.
export const makeLoader = (
  options: {
    getContainerId?: (params: Params) => string | undefined;
    tableId?: string;
    containerTypes?: string[];
  } = {}
): ((userData: AuthContextType | null) => LoaderFunction) => {
  const { getContainerId, tableId = tableIds.MULTIMEDIA, containerTypes } = options;
  return (userData) => async ({ params }) => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  // Banderitas (spec 2026-10-22 § 7.2): en paralelo y sin esperar; nunca lanza.
  void refreshOrderFlags(userData);
  const cursorMultimedia = getCursor(tableId)(store.getState());
  const dispatch = store.dispatch;

  const roleActive = userData?.user?.user?.rolActive;

  // Fase 2 nav Trabajo/Revisión — containerId contextual (id de la Order de
  // la campaña), resincronizado en CADA run del loader.
  const containerId = getContainerId?.(params);
  if (containerId && cursorMultimedia.containerId !== containerId) {
    dispatch(setCursor(tableId, { ...cursorMultimedia, containerId }));
  }

  // Fase 3 nav Trabajo/Revisión (rework) — containerTypes estático,
  // resincronizado en CADA run del loader (mismo criterio que containerId).
  if (
    containerTypes?.length &&
    JSON.stringify(cursorMultimedia.containerTypes) !== JSON.stringify(containerTypes)
  ) {
    dispatch(setCursor(tableId, { ...cursorMultimedia, containerTypes }));
  }

  // Fase 4 nav Trabajo/Revisión — `containerTypes` estático (RV) gana sobre
  // el cursor; en la ruta GLOBAL el valor sale del filtro "Origen"
  // user-driven ya persistido en `cursorMultimedia` (leído ANTES de los
  // dispatches de arriba — mismo criterio que `containerId` más abajo).
  const effectiveContainerTypes = containerTypes?.length
    ? containerTypes
    : cursorMultimedia.containerTypes;

  // Fix A4: Pre-set concessionId when there's only 1 concession to avoid Apollo cache partition splits
  let effectiveConcessionId = cursorMultimedia.concessionId;
  if (!effectiveConcessionId) {
    try {
      const { data: { concessions } } = await client.query({ query: GET_CONCESSIONS });
      if (concessions?.length === 1) {
        effectiveConcessionId = concessions[0].id;
        dispatch(setCursor(tableId, { ...cursorMultimedia, concessionId: effectiveConcessionId }));
      }
    } catch { /* continue without concessionId */ }
  }

  if (userData?.user) {
    // FIX (loader-loading-flag-leak): cierra cualquier flag fugado por un run
    // anterior interrumpido. El inner try/catch ya cubre el throw mid-MISS.
    dispatch(loadingData(tableId, false));

    const readMultimedia = client.readQuery({
      query: FETCH_ORDERS,
      variables: {
        input: {
          take: cursorMultimedia.take,
          isMultimedia: true,
          cursor: cursorMultimedia.cursor,
          sortField: cursorMultimedia.sortField,
          orderBy: cursorMultimedia.orderBy,
          fromDate: cursorMultimedia.fromDate,
          toDate: cursorMultimedia.toDate,
          capitolId: cursorMultimedia.capitolId,
          subCapitolId: cursorMultimedia.subCapitolId,
          predefinedOrderId: cursorMultimedia.predefinedOrderId,
          type: cursorMultimedia.type,
          states: cursorMultimedia.states,
          indexType: cursorMultimedia.indexType,
          indexExpedition: cursorMultimedia.indexExpedition,
          classification: cursorMultimedia.classification,
          roadId: cursorMultimedia.roadId,
          userIds: cursorMultimedia.userIds,
          concessionId: effectiveConcessionId,
          pkInitKm: cursorMultimedia.pkInitKm,
          pkInitMeter: cursorMultimedia.pkInitMeter,
          pkEndKm: cursorMultimedia.pkEndKm,
          pkEndMeter: cursorMultimedia.pkEndMeter,
          description: cursorMultimedia.description,
          // «Solo marcadas» (spec 2026-10-22 § 8.3): true o ausente.
          onlyFlagged: cursorMultimedia.onlyFlagged,
          // GOTCHA (review T1): omitir el campo en vez de null/undefined
          // explícito — un `containerId: null` SÍ entra en la keyArgs de
          // Apollo y partiría la caché histórica sin containerId. Misma
          // regla para `containerTypes` (Fase 3 nav rework).
          ...(containerId && { containerId }),
          ...(effectiveContainerTypes?.length && { containerTypes: effectiveContainerTypes }),
        },
      },
    });

    const pageInfo = readMultimedia?.orders?.pageInfo;

    const isCacheEmpty = JSON.stringify(readMultimedia) === '{}';
    const dataOrderWithMultimedia =
      readMultimedia &&
      !isCacheEmpty &&
      (await selectWorkOrdersWithMultimedia(readMultimedia?.orders?.orders));

    const totalCount = readMultimedia?.orders?.totalCount;

    const [
      {
        data: { capitols },
      },
      {
        data: { subCapitols },
      },
      {
        data: { roads, concessions },
      },
      {
        data: { classifications },
      },
      {
        data: { predefinedOrders },
      },
      {
        data: { users },
      },
    ] = await Promise.all([
      client.query({
        query: FETCH_CAPITOLS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_SUB_CAPITOLS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: GET_SELECTORS_DATA,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_CLASSIFICATIONS,
        fetchPolicy: 'cache-first',
      }),
      client.query({
        query: FETCH_PREDEFINED_ORDERS,
        fetchPolicy: 'cache-first',
      }),
      client.query({ query: FETCH_USERS, fetchPolicy: 'cache-first' }),
    ]);

    const filtersDataOptions = {
      capitols,
      subCapitols,
      roads,
      concessions,
      classifications,
      predefinedOrders,
      users,
    };

    const combinedData = {
      totalCount,
      dataOrderWithMultimedia,
      pageInfo,
      filtersDataOptions,
      roleActive,
    };

    if (!readMultimedia || isCacheEmpty) {
      dispatch(loadingData(tableId, true));
      try {
        let data;
        if (isCacheEmpty) {
          // Tarea #11: respetar el take guardado en cursor (page-size selector).
          const effectiveTake = firstPageTake(cursorMultimedia.take, takeBase);
          data = await client.query({
            query: FETCH_ORDERS,
            variables: {
              input: {
                take: effectiveTake,
                isMultimedia: true,
                cursor: '',
                sortField: cursorMultimedia.sortField,
                orderBy: cursorMultimedia.orderBy ?? 'desc',
                fromDate: cursorMultimedia.fromDate,
                toDate: cursorMultimedia.toDate,
                type: cursorMultimedia.type,
                states: cursorMultimedia.states,
                indexType: cursorMultimedia.indexType,
                indexExpedition: cursorMultimedia.indexExpedition,
                classification: cursorMultimedia.classification,
                capitolId: cursorMultimedia.capitolId,
                roadId: cursorMultimedia.roadId,
                userIds: cursorMultimedia.userIds,
                concessionId: effectiveConcessionId,
                subCapitolId: cursorMultimedia.subCapitolId,
                predefinedOrderId: cursorMultimedia.predefinedOrderId,
                pkInitKm: cursorMultimedia.pkInitKm,
                pkInitMeter: cursorMultimedia.pkInitMeter,
                pkEndKm: cursorMultimedia.pkEndKm,
                pkEndMeter: cursorMultimedia.pkEndMeter,
                description: cursorMultimedia.description,
                onlyFlagged: cursorMultimedia.onlyFlagged,
                ...(containerId && { containerId }),
                ...(effectiveContainerTypes?.length && { containerTypes: effectiveContainerTypes }),
              },
            },
          });

          const setCursorData = {
            take: effectiveTake,
            isMultimedia: true,
            cursor: '',
            sortField: cursorMultimedia.sortField,
            orderBy: cursorMultimedia.orderBy ?? 'desc',
            fromDate: cursorMultimedia.fromDate,
            toDate: cursorMultimedia.toDate,
            type: cursorMultimedia.type,
            states: cursorMultimedia.states,
            indexType: cursorMultimedia.indexType,
            indexExpedition: cursorMultimedia.indexExpedition,
            classification: cursorMultimedia.classification,
            capitolId: cursorMultimedia.capitolId,
            subCapitolId: cursorMultimedia.subCapitolId,
            predefinedOrderId: cursorMultimedia.predefinedOrderId,
            roadId: cursorMultimedia.roadId,
            userIds: cursorMultimedia.userIds,
            concessionId: effectiveConcessionId,
            pkInitKm: cursorMultimedia.pkInitKm,
            pkInitMeter: cursorMultimedia.pkInitMeter,
            pkEndKm: cursorMultimedia.pkEndKm,
            pkEndMeter: cursorMultimedia.pkEndMeter,
            description: cursorMultimedia.description,
            onlyFlagged: cursorMultimedia.onlyFlagged,
            // Redux (no Apollo): full-replace de la slice del cursor — hace
            // falta el valor explícito para no perder el containerId/
            // containerTypes sincronizados al principio del loader.
            containerId,
            containerTypes: effectiveContainerTypes,
          };

          dispatch(setCursor(tableId, setCursorData));
        } else {
          data = await client.query({
            query: FETCH_ORDERS,
            variables: {
              input: {
                take: cursorMultimedia.take,
                isMultimedia: true,
                cursor: cursorMultimedia.cursor,
                sortField: cursorMultimedia.sortField,
                orderBy: cursorMultimedia.orderBy,
                fromDate: cursorMultimedia.fromDate,
                toDate: cursorMultimedia.toDate,
                type: cursorMultimedia.type,
                states: cursorMultimedia.states,
                indexType: cursorMultimedia.indexType,
                indexExpedition: cursorMultimedia.indexExpedition,
                classification: cursorMultimedia.classification,
                capitolId: cursorMultimedia.capitolId,
                subCapitolId: cursorMultimedia.subCapitolId,
                predefinedOrderId: cursorMultimedia.predefinedOrderId,
                roadId: cursorMultimedia.roadId,
                userIds: cursorMultimedia.userIds,
                concessionId: effectiveConcessionId,
                pkInitKm: cursorMultimedia.pkInitKm,
                pkInitMeter: cursorMultimedia.pkInitMeter,
                pkEndKm: cursorMultimedia.pkEndKm,
                pkEndMeter: cursorMultimedia.pkEndMeter,
                description: cursorMultimedia.description,
                onlyFlagged: cursorMultimedia.onlyFlagged,
                ...(containerId && { containerId }),
                ...(effectiveContainerTypes?.length && { containerTypes: effectiveContainerTypes }),
              },
            },
          });
        }

        const loading = data.loading;
        const pageInfo = data.data.orders.pageInfo;

        const dataOrderWithMultimedia = await selectWorkOrdersWithMultimedia(
          data.data.orders.orders
        );

        const totalCount = data.data.orders.totalCount;

        const [
          {
            data: { capitols },
          },
          {
            data: { subCapitols },
          },
          {
            data: { roads: roadsData, concessions },
          },
          {
            data: { classifications },
          },
          {
            data: { predefinedOrders },
          },
          {
            data: { users },
          },
        ] = await Promise.all([
          client.query({
            query: FETCH_CAPITOLS,
            fetchPolicy: 'cache-first',
          }),
          client.query({
            query: FETCH_SUB_CAPITOLS,
            fetchPolicy: 'cache-first',
          }),
          client.query({
            query: GET_SELECTORS_DATA,
            fetchPolicy: 'cache-first',
          }),
          client.query({
            query: FETCH_CLASSIFICATIONS,
            fetchPolicy: 'cache-first',
          }),
          client.query({
            query: FETCH_PREDEFINED_ORDERS,
            fetchPolicy: 'cache-first',
          }),
          client.query({ query: FETCH_USERS, fetchPolicy: 'cache-first' }),
        ]);

        const filtersDataOptions = {
          capitols,
          subCapitols,
          roads: roadsData,
          concessions,
          classifications,
          predefinedOrders,
          users,
        };

        const combinedData = {
          totalCount,
          dataOrderWithMultimedia,
          pageInfo,
          filtersDataOptions,
          loading,
          roleActive,
        };
        dispatch(loadingData(tableId, false));
        return combinedData;
      } catch (error) {
        console.error('[MultimediaLoader] Error loading data:', error);
        dispatch(loadingData(tableId, false));
        return {
            totalCount: 0,
            dataOrderWithMultimedia: [],
            pageInfo: { hasNextPage: false, hasPreviousPage: false },
            filtersDataOptions: null,
            roleActive: null,
          };
      }
    }

    return combinedData;
  }
  return redirect('/login');
  };

  return { data: load() };
};
};

// Loader global (ruta `/work-orders/multimedia`) — comportamiento
// byte-idéntico al anterior a la Fase 2 nav Trabajo/Revisión.
export const LoaderMultimediaGeneralView = makeLoader();
